// ─── Approval guide texts (keyed by approval type) ────────────────────────

export const APPROVAL_GUIDES = {
  manualLog: `**Attendance Approval (Manual Log)**
📍 *Sidebar: Approvals → Manual Log*

Review and approve or reject manual attendance entries submitted by employees or supervisors.

**Tabs / Filters:**
- **All** — All submitted attendance records
- **Pending** — Awaiting your action
- **Approved** — Already approved entries
- **Rejected** — Rejected entries

**Actions available:** Approve, Reject, View details.`,

  overtime: `**Overtime Approval**
📍 *Sidebar: Approvals → Overtime*

Review overtime work requests submitted by employees.

**Tabs / Filters:**
- **All** — All overtime requests
- **Pending** — Awaiting approval
- **Approved** — Approved overtime
- **Rejected** — Rejected requests

**Actions available:** Approve, Reject, View details.`,

  leaveRequests: `**Leave Requests Approval**
📍 *Sidebar: Approvals → Leave Requests*

Manage employee leave applications (annual, casual, sick, etc.).

**Tabs / Filters:**
- **All** — All leave requests
- **Pending** — Awaiting approval
- **Approved** — Approved leaves
- **Rejected** — Rejected leaves

**Actions available:** Approve, Reject, View details.`,

  holidayRequests: `**Holiday Requests Approval**
📍 *Sidebar: Approvals → Holiday Requests*

Review and act on employee holiday/day-off requests.

**Tabs / Filters:**
- **All** — All requests
- **Pending** — Awaiting approval
- **Approved** — Approved holidays
- **Rejected** — Rejected requests

**Actions available:** Approve, Reject, View details.`,

  wfhApproval: `**Work From Home (WFH) Approval**
📍 *Sidebar: Approvals → WFH Approval*

Approve or reject employee work-from-home requests.

**Tabs / Filters:**
- **All** — All WFH requests
- **Pending** — Awaiting approval
- **Approved** — Approved WFH days
- **Rejected** — Rejected requests

**Actions available:** Approve, Reject, View details.`,

  mobileRegistration: `**Mobile Registration Approval**
📍 *Sidebar: Approvals → Mobile Registration*

Review employee requests to register their mobile device for attendance.

**Tabs / Filters:**
- **Pending** — Awaiting approval
- **Approved** — Registered devices
- **Rejected** — Rejected registrations

**Actions available:** Approve or reject device registration requests.`,

  faceRegistration: `**Face Registration Approval**
📍 *Sidebar: Approvals → Face Registration*

Approve or reject employee face recognition enrollment requests.

**Tabs / Filters:**
- **Pending** — Awaiting approval
- **Approved** — Enrolled faces
- **Rejected** — Rejected enrollments

**Actions available:** Approve or reject face registration requests.`,
}

// ─── Configuration guide texts (keyed by config type) ───────────────────────

export const CONFIG_GUIDES = {
  attendance: `**Attendance Configuration**
📍 *Sidebar: Configurations → Attendance*

Configure attendance rules per department, designation, or employee.

**Filter by:**
- Department, Designation, or Employee

**Settings include:**
- Duplicate Punch Interval (minutes)
- Late In Exceed / Early Out Exceed thresholds
- Action when Late In or Early Out exceeds threshold
- Check In Miss / Check Out Miss handling
- Weekend days (Monday → Sunday toggle)
- Working on Weekend rules
- Work From Home settings`,

  approval: `**Approval Configuration**
📍 *Sidebar: Configurations → Approval*

Control which approval workflows are active and how they are structured.

**Settings include:**
- Enable / disable approval types (leave, overtime, WFH, manual log)
- Approval levels (single or multi-level)
- Department-wise or organisation-wide approval routing`,

  email: `**Email Configuration**
📍 *Sidebar: Configurations → Email*

Set up email delivery for system notifications, payslips, and scheduled reports.

**Settings include:**
- SMTP server and credentials
- Sender name and reply-to address
- Email templates for notifications and payslips`,
}

// ─── Employee module guide texts (keyed by employee item key) ─────────────

export const EMPLOYEE_GUIDES = {
  employee: `**Employee**
📍 *Sidebar: Employees → Employee*

Manage all employee records in the organisation.

**Actions available:**
- Add new employees (fill in Employee ID, Name, Branch, Designation, Start Date)
- Edit employee details
- View employee profiles and configuration
- Filter by branch, designation, or status

**Tip:** Before adding an employee, ensure the Branch and Designation already exist.`,

  departments: `**Departments (Branches)**
📍 *Sidebar: Employees → Departments*

Manage organisational branches / departments.

**Fields:**
- **Name** — Branch or department name
- **Organisation** — Parent organisation
- **Location** — Optional address or area
- **Manager** — Assigned manager (optional)

**Tip:** Employees are assigned to a branch during onboarding. Attendance rules can be configured per branch.`,

  designation: `**Designation**
📍 *Sidebar: Employees → Designation*

Define job titles / positions used across the organisation.

**Fields:**
- **Name** — Job title (e.g. Manager, Engineer, Clerk)
- **Organisation** — Owning organisation

**Tip:** Designations are used when mapping payroll profiles (Designation Payroll) to apply salary rules to all employees in a role.`,

  areas: `**Areas**
📍 *Sidebar: Employees → Areas*

Define physical areas or zones used for geo-fencing and attendance tracking.

**Fields:**
- **Name** — Area label (e.g. Head Office, Warehouse)
- **Coordinates** — GPS boundary for geo-fencing

**Tip:** Assign areas to employees via Employee Setup → Geo Fencing to restrict clock-in to approved locations.`,

  resigned_employee: `**Resigned Employee**
📍 *Sidebar: Employees → Resigned Employee*

View and manage employees who have left the organisation.

**Features:**
- Filter resigned employees by branch or date range
- View resignation details and final working date
- Reactivate an employee if needed

**Tip:** Resigning an employee archives their record — payroll history and attendance data are preserved.`,
}

// ─── Work Code guide texts (keyed by work code item key) ──────────────────

export const WORKCODE_GUIDES = {
  work_codes: `**Work Codes**
📍 *Sidebar: Parameters → Work Codes*

Work Codes classify attendance transactions (e.g. Normal Work, Overtime, Leave, Holiday).

**Fields:**
- **Code** — Unique identifier (e.g. NW, OT, LV)
- **Description** — Human-readable label
- **Type** — Category (Normal, Overtime, Leave, etc.)
- **Color** — Visual indicator on schedules and reports

**Tip:** Work Codes are referenced in Pay Codes and attendance reports to categorise time entries.`,

  emp_workcode_setup: `**Employee Work Code Setup**
📍 *Sidebar: Parameters → Emp Work Code Setup*

Assign specific work codes to individual employees, overriding the organisation-level defaults.

**Fields:**
- **Employee** — Select the employee
- **Work Code** — The code to assign
- **Effective Date** — When the assignment takes effect

**Tip:** Use this when an employee has a different work classification from their department or organisation.`,

  dept_workcode_setup: `**Dept Work Code Setup**
📍 *Sidebar: Parameters → Dept Work Code Setup*

Assign work codes at the department (branch) level. Applies to all employees in the department unless overridden at employee level.

**Fields:**
- **Department / Branch** — Select the branch
- **Work Code** — The code to assign
- **Effective Date** — When the assignment takes effect

**Tip:** Employee-level work code setup takes priority over department-level setup.`,
}

// ─── Report category → pages mapping (with RBAC perm keys) ────────────────

// dateParamStyle controls how start/end date is sent to the export endpoint:
//   'fromDate'    → fromDate / toDate
//   'from_date'   → from_date / to_date
//   'monthYear'   → selectedMonth / selectedYear  (derived from startDate)
export const REPORT_CATEGORY_PAGES = {
  daily: [
    {
      label: 'Daily Activity',
      path: '/dailyActivity',
      sidebar: 'Reports → Daily Reports → Daily Activity',
      desc: 'Punch-by-punch activity log for each employee for the selected day.',
      perm: 'canAccessDailyActivity',
      exportExcel: 'attendance/attendance_report/export',
      exportPdf: 'attendance/attendance_report_pdf/export',
      dateParamStyle: 'fromDate',
    },
    {
      label: 'First & Last In/Out',
      path: '/firstLastInOut',
      sidebar: 'Reports → Daily Reports → First & Last In/Out',
      desc: 'First check-in and last check-out time per employee per day.',
      perm: 'canAccessFirstLast',
      exportExcel: 'attendance/attendance/export',
      exportPdf: 'attendance/attendance_pdf/export',
      dateParamStyle: 'fromDate',
    },
    {
      label: 'Multi Worked Hours',
      path: '/multipleWorkedHoursReport',
      sidebar: 'Reports → Daily Reports → Multi Worked Hours',
      desc: 'Total worked hours per employee across multiple shifts for a date range.',
      perm: 'canAccessMultiWorkedHours',
      exportExcel: 'attendance/multiple_worked_hours/export_excel',
      exportPdf: 'attendance/multiple_worked_hours/export_pdf',
      dateParamStyle: 'fromDate',
    },
    {
      label: 'Workcode Report',
      path: '/multipleWorkedHoursWorkCode',
      sidebar: 'Reports → Daily Reports → Workcode Report',
      desc: 'Work-code-wise breakdown of worked hours per employee.',
      perm: 'canAccessWorkcodeReport',
      exportExcel: 'employee/workcode-summary/export',
      exportPdf: 'employee/workcode-summary/pdf/export',
      dateParamStyle: 'fromDate',
    },
  ],
  monthly: [
    {
      label: 'Detailed Report',
      path: '/detailedReport',
      sidebar: 'Reports → Monthly Reports → Detailed Report',
      desc: 'Full monthly attendance details per employee — times, status, OT, late/early minutes.',
      perm: 'canAccessDetailedReport',
      exportExcel: 'attendance/monthly_detailed_report/export',
      exportPdf: 'attendance/monthly_detailed_report_pdf/export',
      dateParamStyle: 'from_date',
    },
    {
      label: 'Monthly Status',
      path: '/monthlyStatus',
      sidebar: 'Reports → Monthly Reports → Monthly Status',
      desc: 'Monthly attendance grid — P/A/L/W/H for each day per employee.',
      perm: 'canAccessMonthlyStatus',
      exportExcel: 'attendance/monthly_detailed_report/export',
      exportPdf: 'attendance/monthly_status_pdf/export',
      dateParamStyle: 'from_date',
    },
    {
      label: 'Monthly Summary',
      path: '/monthlyWorkedHours',
      sidebar: 'Reports → Monthly Reports → Monthly Summary',
      desc: 'Total work hours logged per employee for the selected month.',
      perm: 'canAccessMonthlySummary',
      exportExcel: 'attendance/monthly_detailed_report/export',
      exportPdf: 'attendance/monthly_summary_pdf/export',
      dateParamStyle: 'monthYear',
    },
    {
      label: 'Monthly Overtime',
      path: '/monthlyOvertime',
      sidebar: 'Reports → Monthly Reports → Monthly Overtime',
      desc: 'Total overtime hours per employee for the selected month.',
      perm: 'canAccessMonthlyOvertime',
      exportExcel: 'attendance/monthly_detailed_report/export',
      exportPdf: 'attendance/monthly_overtime_pdf/export',
      dateParamStyle: 'monthYear',
    },
    {
      label: 'Monthly Absence',
      path: '/monthlyAbsence',
      sidebar: 'Reports → Monthly Reports → Monthly Absence',
      desc: 'Summary of absent days per employee for the selected month.',
      perm: 'canAccessMonthlyAbsence',
      exportExcel: 'attendance/monthly_detailed_report/export',
      exportPdf: 'attendance/monthly_absence_pdf/export',
      dateParamStyle: 'monthYear',
    },
  ],
  leave: [
    {
      label: 'Leave Summary',
      path: '/leavesummaryreport',
      sidebar: 'Reports → Leave Reports → Leave Summary',
      desc: 'Summarized leave balances and total days per employee.',
      perm: 'canAccessLeaveSummary',
      exportExcel: 'attendance/leave-summary-report/export',
      exportPdf: 'attendance/leave-summary-report/export',
      dateParamStyle: 'monthYear_leave',
    },
    {
      label: 'Leave Report',
      path: '/leavenewreport',
      sidebar: 'Reports → Leave Reports → Leave Report',
      desc: 'All leave records with status — Pending, Approved, or Rejected.',
      perm: 'canAccessLeaveReport',
      exportExcel: 'attendance/leave/export/',
      exportPdf: 'attendance/leaves/export_pdf',
      dateParamStyle: 'fromDate',
    },
    {
      label: 'Detailed Leave',
      path: '/detailedLeaveReport',
      sidebar: 'Reports → Leave Reports → Detailed Leave',
      desc: 'Detailed leave records with dates, leave types, and approval information.',
      perm: 'canAccessDetailedLeave',
      exportExcel: 'attendance/detail_leaves/export',
      exportPdf: 'attendance/detail_leaves/export',
      dateParamStyle: 'detailedLeave',
    },
  ],
  exception: [
    {
      label: 'Late In Report',
      path: '/lateInReport',
      sidebar: 'Reports → Exception Reports → Late In Report',
      desc: 'Lists employees who arrived late, showing minutes late per day.',
      perm: 'canAccessLateInReport',
      exportExcel: 'attendance/attendance_report/export',
      exportPdf: 'attendance/attendance_report_pdf/export',
      dateParamStyle: 'fromDate',
    },
    {
      label: 'Early Out Report',
      path: '/earlyOutReport',
      sidebar: 'Reports → Exception Reports → Early Out Report',
      desc: 'Lists employees who left early, showing minutes early per day.',
      perm: 'canAccessEarlyOutReport',
      exportExcel: 'attendance/attendance_report/export',
      exportPdf: 'attendance/attendance_report_pdf/export',
      dateParamStyle: 'fromDate',
    },
  ],
  administrative: [
    {
      label: 'Transactions',
      path: '/transactions',
      sidebar: 'Reports → Administrative → Transactions',
      desc: 'Raw punch transaction records from all connected devices.',
      perm: 'canAccessTransactions',
      exportExcel: 'employee/employee_transactions/export',
      exportPdf: 'employee/employee_transactions_pdf/export',
      dateParamStyle: 'fromDate',
    },
  ],
}
